import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { selectStyles } from './selectTheme';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomerAccount from './CustomerAccount';
import { readUtm } from '../tenant';
import IdentityModal from './IdentityModal';
import { ManualPayBoxes } from './ShopAccount';

export default function CustomizationForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toastOpts = { position: 'bottom-right', autoClose: 5000, theme: 'colored' };

  // Step: 'customize' | 'phone' | 'account' | 'address'
  const [step, setStep] = useState('customize');

  // Logged-in customer (account flow for products without customizations)
  const [account, setAccount] = useState(null);

  // Customization
  const [customizations, setCustomizations] = useState({});

  // Phone OTP
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);

  // Address
  const [provinces, setProvinces] = useState([]);
  const [province, setProvince] = useState('');
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // Payment
  const [paidOrder, setPaidOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payMethods, setPayMethods] = useState({ gateways: [], shopCards: [] });
  const [payMethod, setPayMethod] = useState('');
  const [walletBalance, setWalletBalance] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const pickReceipt = f => {
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceipt(f);
    setReceiptPreview(f ? URL.createObjectURL(f) : null);
  };
  const [shopCardIdx, setShopCardIdx] = useState(0);

  // identity gate (admin: site_settings.kyc.profile_required)
  const [kycRequired, setKycRequired] = useState(false);
  const [accountNational, setAccountNational] = useState('');
  const [idModalOpen, setIdModalOpen] = useState(false);
  const [pendingPay, setPendingPay] = useState(null);
  const gateIdentity = resume => {
    if (kycRequired && !accountNational) { setPendingPay(() => resume); setIdModalOpen(true); return true; }
    return false;
  };

  // Saved address book (logged-in customers)
  const [savedAddrs, setSavedAddrs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/products/${productId}`);
        setProduct(res.data);
        const init = {};
        if (res.data.customization_data?.fields) {
          res.data.customization_data.fields.forEach(f => { init[f.name] = f.defaultValue || ''; });
        }
        setCustomizations(init);
        setLoading(false);
      } catch (err) {
        setError('Failed to load product.');
        setLoading(false);
      }
    })();
    // Returning customer? validate token silently → skip phone/account steps later.
    let ct = null;
    try { ct = localStorage.getItem('customer_token'); } catch {}
    if (ct) {
      axios.get('/auth/me', { headers: { Authorization: `Bearer ${ct}` } }).then(r => {
        const u = r.data.user || r.data;
        if (u && u.phone) {
          setPhone(u.phone);
          setAccount({ phone: u.phone, token: ct });
          setAccountNational(u.national_code || '');
          setPhoneVerified(true);
        }
      }).catch(() => { try { localStorage.removeItem('customer_token'); } catch {} });
    }
  }, [productId]);

  const handleInput = (name, val) => setCustomizations(prev => ({ ...prev, [name]: val }));

  const renderField = (f) => {
    switch (f.type) {
      case 'select':
        return (
          <select value={customizations[f.name] || ''} onChange={e => handleInput(f.name, e.target.value)} style={inStyle}>
            {f.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'textarea':
        return <textarea value={customizations[f.name] || ''} onChange={e => handleInput(f.name, e.target.value)} placeholder={f.name} rows={3} style={inStyle} />;
      default:
        return <input type={f.type === 'number' ? 'number' : 'text'} value={customizations[f.name] || ''} onChange={e => handleInput(f.name, e.target.value)} placeholder={f.name} style={inStyle} />;
    }
  };

  const inStyle = { width: '100%', padding: '11px', borderRadius: '8px', border: '1px solid #ccc', boxSizing: 'border-box' };

  // --- OTP flow ---
  const goToPhone = () => {  setStep('phone'); };
  const goToAccount = () => {  setAccount(null); setStep('account'); };

  const onAccountDone = ({ phone: accPhone, token, user }) => {
    setPhone(accPhone);
    setAccount({ phone: accPhone, token });
    setAccountNational(user?.national_code || '');
    setPhoneVerified(true);
    goToAddress(token);
  };

  const sendOtp = async () => {
    
    if (!/^09[0-9]{9}$/.test(phone)) { toast.warn('شماره موبایل معتبر نیست (09xxxxxxxxx).', toastOpts); return; }
    try {
      const res = await axios.post('/auth/send-otp', { phone });
      toast.success('کد تایید ارسال شد.' + (res.data.dev_code ? ' (کد تست: ' + res.data.dev_code + ')' : ''), toastOpts);
    } catch (err) {
      toast.error((err.response?.data?.message || err.message), toastOpts);
    }
  };

  const verifyOtp = async () => {
    
    try {
      const res = await axios.post('/auth/verify-otp', { phone, code: otp });
      setVerificationToken(res.data.verification_token);
      setPhoneVerified(true);
      loadProvinces();
      setStep('address');
    } catch (err) {
      toast.error((err.response?.data?.message || err.message), toastOpts);
    }
  };

  // --- Address ---
  const loadProvinces = async () => {
    try {
      const res = await axios.get('/regions/provinces');
      setProvinces(res.data);
    } catch (err) {
      toast.error('خطا در دریافت استان‌ها.', toastOpts);
    }
  };

  const onProvinceChange = async (val, presetCity) => {
    setProvince(val);
    setCity(presetCity || '');
    if (!val) { setCities([]); return; }
    try {
      const res = await axios.get('/regions/cities', { params: { province: val } });
      setCities(res.data);
      if (presetCity && !(res.data || []).includes(presetCity)) setCity(presetCity);
    } catch (err) {
      setCities([]);
      if (presetCity) setCity(presetCity);
    }
  };

  const loadSavedAddrs = async tok => {
    try {
      const r = await axios.get('/account/summary', { headers: { Authorization: `Bearer ${tok}` } });
      const list = r.data.addresses || [];
      setSavedAddrs(list);
      const d = list.find(a => a.is_default) || list[0];
      if (d) {
        if (d.province) await onProvinceChange(d.province, d.city || '');
        setAddress(d.address || '');
        setPostalCode(d.postal_code || '');
      }
    } catch {}
  };

  const goToAddress = (tok) => {
    loadProvinces();
    if (tok) loadSavedAddrs(tok);
    setStep('address');
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    
    if (!phoneVerified && !account) { toast.warn('ابتدا شماره موبایل را تایید کنید.', toastOpts); return; }
    try {
      const res = await axios.post('/orders', {
        product_id: productId,
        customizations,
        phone,
        verification_token: account ? undefined : verificationToken,
        customer_token: account?.token,
        province,
        city,
        address,
        postal_code: postalCode,
        ...readUtm(),
      });
      toast.success('سفارش شما ثبت شد! کد پیگیری: ' + res.data.id, toastOpts);
      setPaidOrder(res.data);
      loadPayMethods();
      // Logged-in? silently save this address to the book if it's new.
      if (account?.token && address) {
        const dup = savedAddrs.some(a => (a.address || '') === address && (a.postal_code || '') === postalCode);
        if (!dup) {
          axios.post('/account/addresses', { province, city, address, postal_code: postalCode, is_default: !savedAddrs.length },
            { headers: { Authorization: `Bearer ${account.token}` } }).catch(() => {});
        }
      }
    } catch (err) {
      const d = err.response?.data?.message || err.message;
      toast.error(d, toastOpts);
    }
  };

  const gwLabel = g => (g === 'zarinpal' ? 'زرین‌پال' : g === 'zibal' ? 'زیبال' : g);

  const loadPayMethods = async () => {
    try {
      const r = await axios.get('/site-settings');
      const p = r.data.payments || {};
      setKycRequired(!!r.data.kyc?.profile_required);
      const gateways = (p.active && p.active.length ? p.active : [p.gateway || 'zarinpal']).filter(g => g === 'zarinpal' || g === 'zibal');
      setPayMethods({ gateways, shopCards: p.shop_cards || [] });
      if (gateways.length) setPayMethod('gateway:' + gateways[0]);
    } catch {}
    if (account?.token) {
      try {
        const r = await axios.get('/account/summary', { headers: { Authorization: 'Bearer ' + account.token } });
        setWalletBalance(Number(r.data.user?.wallet_balance || 0));
        setAccountNational(r.data.user?.national_code || '');
      } catch {}
    }
  };

  const payOnline = async gateway => {
    if (!paidOrder) return;
    if (gateIdentity(() => payOnline(gateway))) return;
    setPaying(true);
    
    try {
      const res = await axios.post('/payment/request', { order_id: paidOrder.id, gateway });
      window.location.href = res.data.url;
    } catch (err) {
      if (err.response?.data?.need_identity) { setPendingPay(() => () => payOnline(gateway)); setIdModalOpen(true); }
      else toast.error('خطا در اتصال به درگاه: ' + (err.response?.data?.message || err.message), toastOpts);
    }
    setPaying(false);
  };

  const payWithWallet = async () => {
    if (!paidOrder || !account?.token) return;
    if (gateIdentity(payWithWallet)) return;
    setPaying(true);
    
    try {
      const res = await axios.post('/payment/wallet', { order_id: paidOrder.id },
        { headers: { Authorization: 'Bearer ' + account.token } });
      toast.success('پرداخت از کیف پول انجام شد! موجودی جدید: ' + Number(res.data.balance || 0).toLocaleString('fa-IR') + ' تومان', toastOpts);
      setPaidOrder({ ...paidOrder });
      setPayMethod('done');
    } catch (err) {
      if (err.response?.data?.need_identity) { setPendingPay(() => payWithWallet); setIdModalOpen(true); }
      else toast.error((err.response?.data?.message || err.message), toastOpts);
    }
    setPaying(false);
  };

  const submitManual = async () => {
    if (!paidOrder) return;
    if (!receipt) { toast.warn('لطفاً تصویر فیش واریزی را انتخاب کنید.', toastOpts); return; }
    if (gateIdentity(submitManual)) return;
    setPaying(true);
    
    try {
      const fd = new FormData();
      fd.append('order_id', paidOrder.id);
      fd.append('shop_card', shopCardIdx);
      fd.append('receipt', receipt);
      const res = await axios.post('/payment/manual', fd);
      toast.success(res.data.message, toastOpts);
      pickReceipt(null);
      setPayMethod('done');
    } catch (err) {
      if (err.response?.data?.need_identity) { setPendingPay(() => submitManual); setIdModalOpen(true); }
      else toast.error((err.response?.data?.message || err.message), toastOpts);
    }
    setPaying(false);
  };

  if (loading) return <div style={{ padding: '80px', color: '#888' }}>در حال بارگذاری...</div>;
  if (error || !product) return <div style={{ padding: '80px', color: 'red' }}>محصول یافت نشد.</div>;

  const boxStyle = { maxWidth: '560px', margin: '40px auto', padding: '30px', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', backgroundColor: '#fff', direction: 'rtl', textAlign: 'right' };
  const btn = { width: '100%', padding: '13px', borderRadius: '8px', border: 'none', backgroundColor: '#3a3a3a', color: '#fff', fontSize: '1em', cursor: 'pointer' };

  return (
    <div>
      {step === 'customize' && (
        <form style={boxStyle} onSubmit={e => {
          e.preventDefault();
          if (account) { goToAddress(account.token); return; } // logged in → straight to address
          (product.customization_data?.fields?.length ? goToPhone() : goToAccount());
        }}>
          <h2 style={{ margin: '0 0 6px', fontWeight: '400' }}>سفارش {product.name}</h2>
          <p style={{ color: '#888', margin: '0 0 20px', fontSize: '0.9em' }}>مشخصات سفارش خود را وارد کنید</p>
          {account && (
            <p style={{ background: '#F0FDF4', color: '#166534', fontSize: '0.85em', borderRadius: 8, padding: '8px 12px' }}>وارد شده‌اید ({phone}) — بدون احراز مجدد ادامه می‌دهید.</p>
          )}
          {product.customization_data?.fields?.map(f => (
            <div key={f.name} style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>{f.name}</label>
              {renderField(f)}
            </div>
          ))}
          {(!product.customization_data?.fields || product.customization_data.fields.length === 0) && (
            <p style={{ color: '#888', fontSize: '0.9em' }}>این محصول ویژگی سفارشی ندارد — برای ادامه وارد حساب خود شوید.</p>
          )}
          <button type="submit" style={btn}>ادامه و ثبت شماره تماس</button>
        </form>
      )}

      {step === 'account' && (
        <CustomerAccount initialPhone={phone} onDone={onAccountDone} />
      )}

      {step === 'phone' && (
        <form style={boxStyle} onSubmit={e => { e.preventDefault(); verifyOtp(); }}>
          <h2 style={{ margin: '0 0 6px', fontWeight: '400' }}>احراز هویت</h2>
          <p style={{ color: '#888', margin: '0 0 20px', fontSize: '0.9em' }}>شماره موبایل خود را وارد و کد تایید را دریافت کنید</p>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>شماره موبایل</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="09123456789" style={inStyle} />
          </div>
          <button type="button" onClick={sendOtp} style={{ ...btn, backgroundColor: '#616161', marginBottom: '15px' }}>ارسال کد تایید</button>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>کد تایید</label>
            <input type="text" value={otp} onChange={e => setOtp(e.target.value)} placeholder="کد 5 رقمی" style={inStyle} />
          </div>
          <button type="submit" style={btn}>تایید و ادامه</button>
        </form>
      )}

      {step === 'address' && (
        <form style={boxStyle} onSubmit={submitOrder}>
          <h2 style={{ margin: '0 0 6px', fontWeight: '400' }}>آدرس تحویل</h2>
          <p style={{ color: '#888', margin: '0 0 20px', fontSize: '0.9em' }}>آدرس دریافت سفارش در ایران را وارد کنید</p>
          {(account && savedAddrs.length > 0) && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>انتخاب از آدرس‌های ذخیره‌شده</label>
              <select
                value={savedAddrs.findIndex(a => (a.address || '') === address && (a.province || '') === province && (a.city || '') === city)}
                onChange={e => {
                  const d = savedAddrs[Number(e.target.value)];
                  if (!d) return;
                  if (d.province) onProvinceChange(d.province, d.city || '');
                  else { setProvince(''); setCity(d.city || ''); }
                  setAddress(d.address || '');
                  setPostalCode(d.postal_code || '');
                }}
                style={inStyle}>
                {savedAddrs.map((a, i) => (
                  <option key={a.id || i} value={i}>{[a.province, a.city].filter(Boolean).join('، ')} — {(a.address || '').slice(0, 30)}{a.is_default ? ' (پیش‌فرض)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>استان</label>
            <Select
              styles={selectStyles}
              isRtl isSearchable
              placeholder="جستجو و انتخاب استان..."
              options={provinces.map(p => ({ value: p.name || p, label: p.name || p }))}
              value={province ? { value: province, label: province } : null}
              onChange={o => onProvinceChange(o ? o.value : '')}
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>شهر</label>
            <Select
              styles={selectStyles}
              isRtl isSearchable
              isDisabled={!province}
              placeholder={province ? 'جستجو و انتخاب شهر...' : 'اول استان را انتخاب کنید'}
              options={cities.map(c => ({ value: c, label: c }))}
              value={city ? { value: city, label: city } : null}
              onChange={o => setCity(o ? o.value : '')}
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>آدرس کامل</label>
            <textarea value={address} onChange={e => setAddress(e.target.value)} rows={3} style={inStyle} placeholder="خیابان، کوچه، پلاک، واحد" />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>کد پستی (10 رقم)</label>
            <input type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="1234567890" style={inStyle} />
          </div>
          <button type="submit" style={btn}>ثبت نهایی سفارش</button>
        </form>
      )}

      {paidOrder && (
        <div style={{ ...boxStyle, textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 8px', fontWeight: '600' }}>پرداخت سفارش #{paidOrder.id}</h3>
          <p style={{ color: '#888', fontSize: '0.9em', margin: '0 0 6px' }}>مبلغ قابل پرداخت: {Number(paidOrder.price || 0).toLocaleString('fa-IR')} تومان</p>
          {payMethod !== 'done' ? (<>
            <p style={{ fontWeight: 600, fontSize: '0.9em', margin: '14px 0 8px' }}>روش پرداخت را انتخاب کنید:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'right', marginBottom: 14 }}>
              {payMethods.gateways.map(g => (
                <label key={g} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1.5px solid ' + (payMethod === 'gateway:' + g ? 'var(--primary)' : 'var(--border)'), background: payMethod === 'gateway:' + g ? 'var(--muted)' : 'var(--card)', color: 'var(--fg)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: '0.88em', fontWeight: 600 }}>
                  <input type="radio" name="paymethod" checked={payMethod === 'gateway:' + g} onChange={() => setPayMethod('gateway:' + g)} style={{ accentColor: 'var(--primary)' }} />
                  پرداخت آنلاین با {gwLabel(g)}
                </label>
              ))}
              {account?.token && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1.5px solid ' + (payMethod === 'wallet' ? 'var(--primary)' : 'var(--border)'), background: payMethod === 'wallet' ? 'var(--muted)' : 'var(--card)', color: 'var(--fg)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: '0.88em', fontWeight: 600 }}>
                  <input type="radio" name="paymethod" checked={payMethod === 'wallet'} onChange={() => setPayMethod('wallet')} style={{ accentColor: 'var(--primary)' }} />
                  پرداخت از کیف پول {walletBalance !== null && <span style={{ color: 'var(--accent)' }}>(موجودی: {walletBalance.toLocaleString('fa-IR')} تومان)</span>}
                </label>
              )}
              {payMethods.shopCards.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1.5px solid ' + (payMethod === 'manual' ? 'var(--primary)' : 'var(--border)'), background: payMethod === 'manual' ? 'var(--muted)' : 'var(--card)', color: 'var(--fg)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: '0.88em', fontWeight: 600 }}>
                  <input type="radio" name="paymethod" checked={payMethod === 'manual'} onChange={() => setPayMethod('manual')} style={{ accentColor: 'var(--primary)' }} />
                  کارت‌به‌کارت مستقیم + ارسال فیش
                </label>
              )}
            </div>
            {payMethod === 'manual' && payMethods.shopCards.length > 0 && (
              <div style={{ border: '1px dashed var(--border)', background: 'var(--card)', borderRadius: 12, padding: 14, marginBottom: 14, textAlign: 'right' }}>
                <label style={{ display: 'block', marginBottom: 10, fontSize: '0.85em', fontWeight: 700, color: 'var(--fg)' }}>کارت فروشگاه را انتخاب کنید و مبلغ را واریز کنید</label>
                <div style={{ marginBottom: 12 }}>
                  <ManualPayBoxes cards={payMethods.shopCards} selected={shopCardIdx} onSelect={setShopCardIdx} />
                </div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85em', fontWeight: 700, color: 'var(--fg)' }}>تصویر فیش واریزی</label>
                <input type="file" accept="image/*" onChange={e => pickReceipt(e.target.files[0] || null)} style={{ ...inStyle, background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--fg)' }} />
                {receiptPreview && (
                  <div style={{ position: 'relative', marginBottom: 10 }}>
                    <img src={receiptPreview} alt="پیش‌نمایش فیش" style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--muted)' }} />
                    <button onClick={() => pickReceipt(null)} aria-label="حذف فیش"
                      style={{ position: 'absolute', top: 8, left: 8, width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', lineHeight: 1 }}>×</button>
                  </div>
                )}
              </div>
            )}
            {payMethod.startsWith('gateway:') && (
              <button onClick={() => payOnline(payMethod.split(':')[1])} disabled={paying} style={btn}>{paying ? 'در حال اتصال...' : 'پرداخت آنلاین'}</button>
            )}
            {payMethod === 'wallet' && (
              <button onClick={payWithWallet} disabled={paying || (walletBalance !== null && walletBalance < Number(paidOrder.price || 0))} style={btn}>
                {paying ? 'در حال پرداخت...' : (walletBalance !== null && walletBalance < Number(paidOrder.price || 0) ? 'موجودی کافی نیست — ابتدا شارژ کنید' : 'پرداخت از کیف پول')}
              </button>
            )}
            {payMethod === 'manual' && (
              <button onClick={submitManual} disabled={paying} style={btn}>{paying ? 'در حال ارسال...' : 'ثبت فیش واریزی'}</button>
            )}
          </>) : (
            <p style={{ color: '#166534', fontWeight: 600 }}>سفارش شما تعیین تکلیف شد.</p>
          )}
          <div style={{ marginTop: 12 }}>
            <button onClick={() => navigate('/')} style={{ ...btn, backgroundColor: '#fff', color: '#3a3a3a', border: '1px solid #ccc' }}>بازگشت به فروشگاه</button>
          </div>
        </div>
      )}
      <IdentityModal open={idModalOpen} onClose={() => { setIdModalOpen(false); setPendingPay(null); }}
        note="برای پرداخت سفارش، ابتدا احراز هویت (کد ملی و تاریخ تولد) را تکمیل کنید."
        onVerified={u => { setAccountNational(u?.national_code || ''); setIdModalOpen(false); const fn = pendingPay; setPendingPay(null); if (fn) fn(); }} />
      <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" style={{ zIndex: 99999 }} />
    </div>
  );
}
